import React, { useState } from 'react';
import { CrownIcon, WalletIcon, ZapIcon, PlusIcon, CheckIcon, MessageCircleIcon, MousePointerClickIcon, PhoneIcon, BarChart2Icon, SparklesIcon } from 'lucide-react';
import { useBillingCart } from '../../context/BillingCartContext';
import { FloatingCart } from '../../components/billing/FloatingCart';
// Pricing Data
const PLANS = [{
  id: 'basic',
  name: 'Basic',
  price: 2999,
  features: ['Core Lead Management', 'Basic Analytics', '1 User']
}, {
  id: 'pro',
  name: 'Pro',
  price: 4999,
  features: ['Advanced Analytics', '5 Users', 'WhatsApp Integration', 'Workflows']
}, {
  id: 'enterprise',
  name: 'Enterprise',
  price: 7999,
  features: ['All Pro Features', 'AI Voice Agents', 'Dedicated Support', 'Custom Integrations']
}];
const WALLET_OPTIONS = [{
  id: 'w5k',
  amount: 5000,
  label: '₹5,000'
}, {
  id: 'w10k',
  amount: 10000,
  label: '₹10,000'
}, {
  id: 'w25k',
  amount: 25000,
  label: '₹25,000'
}, {
  id: 'w50k',
  amount: 50000,
  label: '₹50,000'
}];
const ADDONS = [{
  id: 'whatsapp',
  name: 'WhatsApp',
  icon: MessageCircleIcon,
  tiers: [{
    id: 'wa-starter',
    name: 'Starter',
    price: 499,
    limit: '500 msgs'
  }, {
    id: 'wa-pro',
    name: 'Professional',
    price: 1299,
    limit: '2,000 msgs'
  }, {
    id: 'wa-ent',
    name: 'Enterprise',
    price: 3999,
    limit: '10,000 msgs'
  }]
}, {
  id: 'ctwa',
  name: 'CTWA Ads',
  icon: MousePointerClickIcon,
  tiers: [{
    id: 'ctwa-starter',
    name: 'Starter',
    price: 799,
    limit: '1,000 clicks'
  }, {
    id: 'ctwa-pro',
    name: 'Professional',
    price: 2499,
    limit: '5,000 clicks'
  }, {
    id: 'ctwa-ent',
    name: 'Enterprise',
    price: 5999,
    limit: 'Unlimited'
  }]
}, {
  id: 'voice',
  name: 'AI Voice',
  icon: PhoneIcon,
  tiers: [{
    id: 'voice-in',
    name: 'Inbound',
    price: 1999,
    limit: '500 calls'
  }, {
    id: 'voice-out',
    name: 'Outbound',
    price: 2499,
    limit: '500 calls'
  }, {
    id: 'voice-both',
    name: 'Both',
    price: 3999,
    limit: '500 each'
  }]
}, {
  id: 'analysis',
  name: 'Call Analysis',
  icon: BarChart2Icon,
  tiers: [{
    id: 'ca-basic',
    name: 'Basic',
    price: 499,
    limit: '100 calls'
  }, {
    id: 'ca-pro',
    name: 'Professional',
    price: 1299,
    limit: '500 calls'
  }, {
    id: 'ca-ent',
    name: 'Enterprise',
    price: 2999,
    limit: 'Unlimited'
  }]
}];
export function Billing() {
  const {
    addItem,
    items,
    walletBalance
  } = useBillingCart();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [selectedWallet, setSelectedWallet] = useState<string | null>(null);
  const [selectedAddons, setSelectedAddons] = useState<Record<string, string>>({});
  const currentPlan = 'pro'; // Mock current plan
  const handleSelectPlan = (planId: string) => {
    if (planId === currentPlan) return;
    const plan = PLANS.find(p => p.id === planId);
    if (!plan) return;
    setSelectedPlan(planId);
    addItem({
      id: `plan-${planId}`,
      type: 'subscription',
      name: `${plan.name} Plan`,
      description: 'Monthly subscription',
      price: plan.price
    });
  };
  const handleSelectWallet = (optionId: string) => {
    const option = WALLET_OPTIONS.find(o => o.id === optionId);
    if (!option) return;
    setSelectedWallet(optionId);
    addItem({
      id: `wallet-${optionId}`,
      type: 'wallet',
      name: 'Media Budget Top-up',
      description: `Add ${option.label} to wallet`,
      price: option.amount
    });
  };
  const handleSelectAddon = (addonId: string, tierId: string) => {
    const addon = ADDONS.find(a => a.id === addonId);
    const tier = addon?.tiers.find(t => t.id === tierId);
    if (!addon || !tier) return;
    setSelectedAddons(prev => ({
      ...prev,
      [addonId]: tierId
    }));
    addItem({
      id: tierId,
      type: 'addon',
      name: addon.name,
      description: `${tier.name} tier`,
      price: tier.price,
      details: tier.limit
    });
  };
  const isInCart = (id: string) => items.some(item => item.id === id);
  return <div className="space-y-8 pb-24" data-id="element-2479">
      <div data-id="element-2480">
        <h1 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-2481">
          Billing & Subscription
        </h1>
        <p className="text-gray-500" data-id="element-2482">
          Select what you need and pay together or separately
        </p>
      </div>

      {/* Section 1: Subscription Plans */}
      <section data-id="element-2483">
        <div className="flex items-center gap-2 mb-4" data-id="element-2484">
          <CrownIcon className="text-amber-500" size={20} data-id="element-2485" />
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-2486">
            Subscription Plan
          </h2>
          <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full" data-id="element-2487">
            Current: Pro
          </span>
        </div>

        <div className="grid grid-cols-3 gap-4" data-id="element-2488">
          {PLANS.map(plan => {
          const isCurrent = plan.id === currentPlan;
          const isSelected = selectedPlan === plan.id || isInCart(`plan-${plan.id}`);
          return <div key={plan.id} className={`relative rounded-xl border-2 p-5 transition-all cursor-pointer ${isCurrent ? 'border-blue-500 bg-blue-50/50' : isSelected ? 'border-green-500 bg-green-50/50' : 'border-gray-200 hover:border-gray-300 bg-white'}`} onClick={() => !isCurrent && handleSelectPlan(plan.id)} data-id="element-2489">
                {isCurrent && <span className="absolute -top-2.5 left-4 px-2 py-0.5 bg-blue-500 text-white text-[10px] font-bold rounded uppercase" data-id="element-2490">
                    Current
                  </span>}
                {isSelected && !isCurrent && <span className="absolute -top-2.5 right-4 px-2 py-0.5 bg-green-500 text-white text-[10px] font-bold rounded flex items-center gap-1" data-id="element-2491">
                    <CheckIcon size={10} data-id="element-2492" /> In Cart
                  </span>}

                <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-2493">
                  {plan.name}
                </h3>
                <div className="flex items-baseline gap-1 mb-4" data-id="element-2494">
                  <span className="text-2xl font-bold text-gray-900" data-id="element-2495">
                    ₹{plan.price.toLocaleString()}
                  </span>
                  <span className="text-sm text-gray-500" data-id="element-2496">/mo</span>
                </div>

                <ul className="space-y-2" data-id="element-2497">
                  {plan.features.map((feature, idx) => <li key={idx} className="flex items-center gap-2 text-sm text-gray-600" data-id="element-2498">
                      <CheckIcon size={14} className="text-green-500" data-id="element-2499" />
                      {feature}
                    </li>)}
                </ul>

                {!isCurrent && <button className={`mt-4 w-full py-2 rounded-lg text-sm font-medium transition-colors ${isSelected ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2500">
                    {isSelected ? 'Added to Cart' : 'Select Plan'}
                  </button>}
              </div>;
        })}
        </div>
      </section>

      {/* Section 2: Media Budget Wallet */}
      <section data-id="element-2501">
        <div className="flex items-center gap-2 mb-4" data-id="element-2502">
          <WalletIcon className="text-emerald-500" size={20} data-id="element-2503" />
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-2504">
            Media Budget Wallet
          </h2>
          <span className="ml-2 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full" data-id="element-2505">
            Balance: ₹{walletBalance.toLocaleString()}
          </span>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5" data-id="element-2506">
          <p className="text-sm text-gray-600 mb-4" data-id="element-2507">
            Add funds to your media wallet for running campaigns
          </p>

          <div className="grid grid-cols-4 gap-3" data-id="element-2508">
            {WALLET_OPTIONS.map(option => {
            const isSelected = selectedWallet === option.id || isInCart(`wallet-${option.id}`);
            return <button key={option.id} onClick={() => handleSelectWallet(option.id)} className={`py-4 rounded-lg border-2 font-semibold transition-all ${isSelected ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 hover:border-emerald-300 text-gray-700'}`} data-id="element-2509">
                  <span className="text-lg" data-id="element-2510">{option.label}</span>
                  {isSelected && <span className="block text-xs text-green-600 mt-1" data-id="element-2511">
                      ✓ In Cart
                    </span>}
                </button>;
          })}
          </div>

          <div className="mt-4 flex items-center gap-2" data-id="element-2512">
            <input type="number" placeholder="Or enter custom amount (min ₹1,000)" className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" data-id="element-2513" />
            <button className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors" data-id="element-2514">
              Add Custom
            </button>
          </div>
        </div>
      </section>

      {/* Section 3: Add-on Services */}
      <section data-id="element-2515">
        <div className="flex items-center gap-2 mb-4" data-id="element-2516">
          <ZapIcon className="text-purple-500" size={20} data-id="element-2517" />
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-2518">
            Add-on Services
          </h2>
        </div>

        <div className="grid grid-cols-2 gap-4" data-id="element-2519">
          {ADDONS.map(addon => {
          const Icon = addon.icon;
          return <div key={addon.id} className="bg-white rounded-xl border border-gray-200 p-5" data-id="element-2520">
                <div className="flex items-center gap-3 mb-4" data-id="element-2521">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center" data-id="element-2522">
                    <Icon className="text-purple-600" size={20} data-id="element-2523" />
                  </div>
                  <h3 className="font-semibold text-gray-900" data-id="element-2524">{addon.name}</h3>
                </div>

                <div className="space-y-2" data-id="element-2525">
                  {addon.tiers.map(tier => {
                const isSelected = selectedAddons[addon.id] === tier.id || isInCart(tier.id);
                return <button key={tier.id} onClick={() => handleSelectAddon(addon.id, tier.id)} className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-purple-300'}`} data-id="element-2526">
                        <div className="text-left" data-id="element-2527">
                          <p className="text-sm font-medium text-gray-900" data-id="element-2528">
                            {tier.name}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-2529">{tier.limit}</p>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-2530">
                          <span className="text-sm font-bold text-gray-900" data-id="element-2531">
                            ₹{tier.price}
                          </span>
                          {isSelected && <CheckIcon size={16} className="text-green-500" data-id="element-2532" />}
                        </div>
                      </button>;
              })}
                </div>
              </div>;
        })}
        </div>
      </section>

      {/* Floating Cart */}
      <FloatingCart data-id="element-2533" />
    </div>;
}